import { Badge } from '@components/ui/badge';
import type { BadgeProps } from '@components/ui/badge';
import { cn } from '@lib/utils';
import type { FC } from 'react';
import { useState } from 'react';
import { TooltipComponent } from '../core/tooltip.component';

type BadgeListProps = {
  badgeClassName?: string;
  badgeVariant?: BadgeProps['variant'];
  items?: { id: string; name: string; tooltip?: string }[];
  limit?: number;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  tooltipMode?: 'badge' | 'icon';
};

export const BadgeList: FC<BadgeListProps> = (props) => {
  const { badgeClassName, badgeVariant = 'default', items, limit = 2, size = 'md', tooltipMode = 'icon' } = props;
  const [isExpanded, setIsExpanded] = useState(false);
  if (!items || items.length === 0) return null;
  const visibleItems = isExpanded ? items : items.slice(0, limit);
  const toggleExpand = () => setIsExpanded(!isExpanded);

  return (
    <div className="flex flex-wrap gap-2">
      {visibleItems.map((item) => {
        const badge = (
          <Badge
            key={item.id}
            variant={badgeVariant}
            size={size}
            className={cn(item.tooltip && tooltipMode === 'badge' && 'cursor-help', badgeClassName)}
          >
            {item.name}
            {item.tooltip && tooltipMode === 'icon' && (
              <TooltipComponent
                side="right"
                className="text-xs whitespace-pre-wrap font-normal"
              >
                {item.tooltip}
              </TooltipComponent>
            )}
          </Badge>
        );

        if (!item.tooltip || tooltipMode === 'icon') {
          return badge;
        }

        return (
          <TooltipComponent
            key={item.id}
            side="right"
            className="text-xs whitespace-pre-wrap font-normal"
            trigger={badge}
          >
            {item.tooltip}
          </TooltipComponent>
        );
      })}
      {items.length > limit && (
        <Badge
          variant={badgeVariant}
          className={cn('cursor-pointer', badgeClassName)}
          onClick={toggleExpand}
          size={size}
        >
          {isExpanded ? 'Show less' : `Show ${items.length - limit}+ more`}
        </Badge>
      )}
    </div>
  );
};
